import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Play, 
  CheckCircle, 
  ChevronLeft, 
  ChevronRight,
  List,
  X,
  Award
} from 'lucide-react';

// Extract YouTube video ID
const getYouTubeId = (url) => {
  const match = url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

export const LearnPage = () => {
  const { courseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true);

  const episodeId = searchParams.get('episode');

  useEffect(() => {
    if (!user) {
      navigate('/connexion', { state: { from: { pathname: `/apprendre/${courseId}` } } });
      return;
    }
    loadData();
  }, [courseId, user]);

  useEffect(() => {
    if (course?.episodes && !currentEpisode) {
      const ep = episodeId 
        ? course.episodes.find(e => e.id === episodeId)
        : course.episodes.sort((a, b) => a.order - b.order)[0];
      setCurrentEpisode(ep);
    }
  }, [course, episodeId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [courseData, enrollments] = await Promise.all([
        api.getCourse(courseId),
        api.getEnrollments()
      ]);

      const found = enrollments.find(e => e.course_id === courseId);
      if (!found) {
        toast.error('Vous n\'êtes pas inscrit à cette formation');
        navigate(`/formation/${courseId}`);
        return;
      }

      setCourse(courseData);
      setEnrollment(found);
    } catch (error) {
      console.error('Error loading course:', error);
      toast.error('Erreur lors du chargement');
      navigate('/mes-formations');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!currentEpisode || !enrollment) return;

    const newCompleted = !enrollment.progress?.[currentEpisode.id];
    
    try {
      await api.updateProgress(enrollment.id, currentEpisode.id, newCompleted);
      
      setEnrollment(prev => ({
        ...prev,
        progress: {
          ...prev.progress,
          [currentEpisode.id]: newCompleted
        }
      }));

      if (newCompleted) {
        toast.success('Épisode marqué comme terminé !');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const goToEpisode = (ep) => {
    setCurrentEpisode(ep);
    setSearchParams({ episode: ep.id });
  };

  const goToPrevious = () => {
    if (!course?.episodes || !currentEpisode) return;
    const sorted = course.episodes.sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(e => e.id === currentEpisode.id);
    if (idx > 0) goToEpisode(sorted[idx - 1]);
  };

  const goToNext = () => {
    if (!course?.episodes || !currentEpisode) return;
    const sorted = course.episodes.sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(e => e.id === currentEpisode.id);
    if (idx < sorted.length - 1) goToEpisode(sorted[idx + 1]);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-beige border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!course || !enrollment || !currentEpisode) {
    return null;
  }

  const sortedEpisodes = course.episodes.sort((a, b) => a.order - b.order);
  const currentIndex = sortedEpisodes.findIndex(e => e.id === currentEpisode.id);
  const completedCount = Object.values(enrollment.progress || {}).filter(Boolean).length;
  const progress = (completedCount / sortedEpisodes.length) * 100;
  const isCompleted = enrollment.progress?.[currentEpisode.id];
  const youtubeId = getYouTubeId(currentEpisode.youtube_url);

  return (
    <div className="min-h-screen pt-16 bg-black">
      <div className="flex h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <div className={`${showSidebar ? 'w-80' : 'w-0'} flex-shrink-0 bg-white border-r border-[#1F2A4A]/10 overflow-hidden transition-all duration-300`}>
          <div className="w-80 h-full flex flex-col">
            {/* Course header */}
            <div className="p-4 border-b border-[#1F2A4A]/10">
              <h2 className="font-heading text-lg font-semibold truncate">{course.title}</h2>
              <div className="flex items-center gap-2 mt-2">
                <Progress value={progress} className="h-2 flex-1" />
                <span className="text-xs text-muted-foreground">{Math.round(progress)}%</span>
              </div>
              {progress === 100 && (
                <div className="flex items-center gap-2 mt-2 text-green-500 text-sm">
                  <Award className="w-4 h-4" />
                  Formation terminée !
                </div>
              )}
            </div>

            {/* Episodes list */}
            <div className="flex-1 overflow-y-auto">
              {sortedEpisodes.map((episode, index) => {
                const isActive = episode.id === currentEpisode.id;
                const isDone = enrollment.progress?.[episode.id];

                return (
                  <button
                    key={episode.id}
                    onClick={() => goToEpisode(episode)}
                    className={`w-full p-4 text-left border-b border-[#1F2A4A]/10 hover:bg-navy-light/50 transition-colors ${
                      isActive ? 'bg-navy-light' : ''
                    }`}
                    data-testid={`sidebar-episode-${episode.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isDone ? 'bg-green-500/20' : isActive ? 'bg-[#C9B896]/20' : 'bg-navy-light'
                      }`}>
                        {isDone ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <span className="text-xs font-mono">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isActive ? 'text-[#C9B896]' : ''}`}>
                          {episode.title}
                        </p>
                        <p className="text-xs text-muted-foreground">{episode.duration_minutes} min</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-[#1F2A4A]/10">
            <button 
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 hover:bg-navy-light rounded-lg transition-colors"
              data-testid="toggle-sidebar"
            >
              {showSidebar ? <X className="w-5 h-5" /> : <List className="w-5 h-5" />}
            </button>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={goToPrevious}
                disabled={currentIndex === 0}
                data-testid="prev-episode"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={goToNext}
                disabled={currentIndex === sortedEpisodes.length - 1}
                data-testid="next-episode"
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>

          {/* Video player */}
          <div className="flex-1 flex flex-col overflow-y-auto">
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              {youtubeId ? (
                <iframe
                  className="absolute inset-0 w-full h-full video-cinema"
                  src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                  title={currentEpisode.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-white">
                  <p className="text-muted-foreground">Vidéo non disponible</p>
                </div>
              )}
            </div>

            {/* Episode info */}
            <div className="p-6 bg-[#FAF7F0]">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="text-sm text-[#C9B896] font-mono uppercase tracking-wider mb-1">
                      Épisode {currentEpisode.order} sur {sortedEpisodes.length}
                    </div>
                    <h1 className="font-heading text-2xl md:text-3xl font-semibold">
                      {currentEpisode.title}
                    </h1>
                  </div>
                  <Button
                    onClick={handleMarkComplete}
                    variant={isCompleted ? 'default' : 'outline'}
                    className={isCompleted ? 'bg-green-600 hover:bg-green-700' : 'border-beige/50'}
                    data-testid="mark-complete"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {isCompleted ? 'Terminé' : 'Marquer comme terminé'}
                  </Button>
                </div>
                
                <p className="text-muted-foreground">
                  {currentEpisode.description}
                </p>

                {/* Next episode prompt */}
                {isCompleted && currentIndex < sortedEpisodes.length - 1 && (
                  <div className="mt-6 p-4 bg-white rounded-lg border border-[#1F2A4A]/10">
                    <p className="text-sm text-muted-foreground mb-2">Prochaine leçon</p>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{sortedEpisodes[currentIndex + 1].title}</span>
                      <Button size="sm" onClick={goToNext} className="bg-[#C9B896] text-black hover:bg-[#E0D4BA]">
                        Continuer
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
